import React from 'react';

export interface ASVerificationBadgeProps {
  asVerified: boolean;
  membershipStatus?: string;
  verificationMatchStatus?: string;
  verifying?: boolean;
  onClick?: () => void;
}

/**
 * Displays the AS number verification status as a colored badge.
 * All states are clickable when onClick is provided — verified badges show "Re-verify" on hover.
 */
const ASVerificationBadge: React.FC<ASVerificationBadgeProps> = ({
  asVerified, membershipStatus, verificationMatchStatus, verifying, onClick,
}) => {
  if (verifying) {
    return (
      <span className="as-verification-badge verifying" role="status" aria-label="Verification in progress">
        <span className="as-verification-spinner" aria-hidden="true" />
        Verifying...
      </span>
    );
  }

  if (asVerified && membershipStatus === 'Active') {
    const Tag = onClick ? 'button' : 'span';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        className={`as-verification-badge verified-active${onClick ? ' clickable re-verify' : ''}`}
        onClick={onClick}
        role="status"
        aria-label={onClick ? 'Verified - Active. Click to re-verify' : 'Verified - Active membership'}
        title={onClick ? 'Click to re-verify' : undefined}
      >
        ✓ Verified - Active
      </Tag>
    );
  }

  if (asVerified && membershipStatus === 'Inactive') {
    const Tag = onClick ? 'button' : 'span';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        className={`as-verification-badge verified-inactive${onClick ? ' clickable re-verify' : ''}`}
        onClick={onClick}
        role="status"
        aria-label={onClick ? 'Verified - Inactive. Click to re-verify' : 'Verified - Inactive membership'}
        title={onClick ? 'Click to re-verify' : undefined}
      >
        ✓ Verified - Inactive
      </Tag>
    );
  }

  if (verificationMatchStatus === 'mismatch') {
    const Tag = onClick ? 'button' : 'span';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        className={`as-verification-badge mismatch${onClick ? ' clickable' : ''}`}
        onClick={onClick}
        role="status"
        title={onClick ? 'AS number mismatch — click to re-verify' : 'AS number mismatch'}
      >
        ⚠ Mismatch
      </Tag>
    );
  }

  if (verificationMatchStatus === 'not_found') {
    const Tag = onClick ? 'button' : 'span';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        className={`as-verification-badge not-found${onClick ? ' clickable' : ''}`}
        onClick={onClick}
        role="status"
        title={onClick ? 'Not found — click to re-verify' : 'AS number not found'}
      >
        ✗ Not Found
      </Tag>
    );
  }

  if (verificationMatchStatus === 'error') {
    const Tag = onClick ? 'button' : 'span';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        className={`as-verification-badge verification-error${onClick ? ' clickable' : ''}`}
        onClick={onClick}
        role="status"
        title={onClick ? 'Error — click to retry' : 'Verification error'}
      >
        ⚠ Error
      </Tag>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className="as-verification-badge unverified clickable"
        onClick={onClick}
        aria-label="Click to verify AS number"
        title="Click to verify"
      >
        Unverified
      </button>
    );
  }

  return (
    <span className="as-verification-badge unverified" role="status">
      Unverified
    </span>
  );
};

export default ASVerificationBadge;
